import { useState } from 'react';
import { motion } from 'framer-motion';
import type { Hero } from '../game/data/heroes';
import { getTribeDefinition } from '../game/data/tribes';
import { getTribeVisualSpec } from './procedural/tribeVisualSpec';
import { getAccessoryComponent } from './procedural/accessoryRegistry';
import { EDGES } from '../theme/tokens';
import { bustPath } from './portraitArt';
import { useHeroDetailStore } from '../stores/heroDetail';

// Procedural hero card — no portrait art assets. Identity comes from
// faction font + tribe color + initials block, drawn from data only.
// This is the cosmetic-as-function bet in practice.

interface HeroCardProps {
  hero: Hero;
  selected: boolean;
  onSelect: () => void;
}

function hexFromColorInt(n: number): string {
  return `#${n.toString(16).padStart(6, '0')}`;
}

function getInitials(name: string): string {
  const parts = name.split(/[\s-]+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function HeroCard({ hero, selected, onSelect }: HeroCardProps) {
  const tribe = hero.tribe ? getTribeDefinition(hero.tribe) : undefined;
  const initials = getInitials(hero.name);
  const isCholo = hero.faction === 'cholo';
  const factionFont = isCholo ? 'font-cholo' : 'font-emo';
  const factionTextColor = isCholo ? 'text-cholo' : 'text-emo';
  const tribeColor = tribe ? hexFromColorInt(tribe.color) : '#8a7e6c';

  // Procedural accessory layer — see CardTile for the pattern.
  const visualSpec = getTribeVisualSpec(hero.tribe);
  const [bustFailed, setBustFailed] = useState(false);
  const openHeroDetail = useHeroDetailStore((s) => s.open);
  const Accessory = visualSpec ? getAccessoryComponent(visualSpec.accessoryId) : null;

  return (
    // The expand trigger is a SIBLING of the card, not a child: the card
    // itself is a <button>, and nesting one button inside another is invalid
    // HTML and swallows the inner click. The wrapper takes the grid slot.
    <div className="relative flex">
    <motion.button
      onClick={onSelect}
      whileTap={{ scale: 0.97 }}
      whileHover={{ y: -2 }}
      transition={{ duration: 0.15 }}
      className={`group relative flex flex-col rounded-md border bg-stucco1 p-sm text-left transition-colors hover:bg-stucco2 md:p-lg ${
        selected ? 'ring-2 ring-sodium' : ''
      }`}
      style={{
        borderColor: selected ? '#ff8a3d' : EDGES.soft,
      }}
      aria-pressed={selected}
    >
      {/* Procedural portrait stencil — initials painted in tribe color over
          asphalt. Portrait shrinks on short viewports (landscape phones) so
          all 4 hero cards + their text + the confirm bar fit without scroll.
          Uses a viewport-height media query because width-based breakpoints
          can't distinguish landscape phone (852×393) from tablet (1024×768). */}
      <div
        className="relative aspect-square w-full overflow-hidden rounded-sm [@media(max-height:500px)]:max-h-[80px]"
        style={{
          // Real portraits are "grounded" — opaque, with their own paper and a flat block of the
          // tribe's colour behind the figure. Nothing of this gradient would show through, so it
          // is only painted for the procedural stencil fallback below.
          background: hero.portrait
            ? undefined
            : `radial-gradient(circle at 30% 25%, rgba(255,138,61,0.05) 0%, #130f0a 75%)`,
        }}
      >
        {hero.portrait ? (
          /* Real art asset (Nano Banana pipeline, tools/README-image-prompts.md).
             The square BUST derivative, not the full page — see portraitArt.ts. The page is a
             3:4 full-body drawing with paper margins; top-anchoring it into this square slot
             still left the face around 20px at the sizes this tile actually renders at, so every
             hero read as "small person on coloured paper". The bust is pre-cropped to head and
             shoulders with the crop found per drawing, so it needs no positioning here.
             Falls back to the full page if the bust has not been built yet — a hero should
             degrade to the wrong crop, never to no portrait.
             Every procedural marker below (tape strip, accessory, initials) is suppressed; the
             drawing carries the identity. */
          <img
            src={`${import.meta.env.BASE_URL}${
              bustFailed ? hero.portrait : bustPath(hero.portrait)
            }`}
            alt=""
            aria-hidden
            onError={() => setBustFailed(true)}
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
        ) : (
          <>
            {/* Tape strip — bottom-right, complementary (kept as compositional anchor) */}
            <div
              aria-hidden
              className="absolute -bottom-1 right-3 h-2 w-10 rotate-2 opacity-50"
              style={{ backgroundColor: tribeColor }}
            />
            {/* Signature accessory — top-right, larger than CardTile since hero
                cards have more real estate and the player is choosing identity here. */}
            {Accessory && visualSpec ? (
              <div
                aria-hidden
                className="absolute right-[6px] top-[6px] z-[1] opacity-95"
                style={{ width: 64, height: 64 }}
              >
                <Accessory
                  bodyColor={visualSpec.paletteHexPrimary}
                  accentColor={visualSpec.paletteHexAccent}
                  size={64}
                  className="h-full w-full drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)] md:h-20 md:w-20 lg:h-24 lg:w-24"
                />
              </div>
            ) : (
              /* Fallback tape strip if no accessory primitive available yet */
              <div
                aria-hidden
                className="absolute -top-1 left-3 h-3 w-12 -rotate-3 opacity-80"
                style={{ backgroundColor: tribeColor }}
              />
            )}
            {/* The initials — the procedural fallback portrait */}
            <span
              className={`absolute inset-0 flex items-center justify-center text-[72px] leading-none md:text-[96px] lg:text-[120px] ${factionFont}`}
              style={{ color: tribeColor }}
            >
              {initials}
            </span>
          </>
        )}
        {/* Faction watermark */}
        <span
          className="absolute bottom-1 left-2 font-data text-[9px] uppercase tracking-[0.3em] text-graphite md:text-[10px]"
        >
          {hero.faction}
        </span>
      </div>

      <span
        className={`mt-xs text-[14px] leading-tight md:mt-md md:text-[24px] lg:text-[28px] ${factionFont} ${factionTextColor}`}
      >
        {hero.name}
      </span>

      {tribe && (
        <span
          className="mt-xs hidden font-data text-[10px] uppercase tracking-[0.2em] md:inline md:text-[11px]"
          style={{ color: tribeColor }}
        >
          {tribe.name}
        </span>
      )}

      {/* Power description clamped to 2 lines so all 4 hero cards stay
          the same height regardless of description length — keeps the
          landscape-mobile row fitting in 393px tall. Full text still
          surfaces in the StrategyTip block below the grid when selected. */}
      <span
        className="mt-xs text-[11px] leading-snug text-marker md:mt-sm md:text-[13px] lg:text-[14px]"
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {hero.powerDescription}
      </span>

      {hero.isActive && hero.cost ? (
        <span className="mt-xs font-data text-[10px] uppercase tracking-[0.2em] text-sodium md:text-[11px]">
          Active · {hero.cost}g
        </span>
      ) : (
        <span className="mt-xs font-data text-[10px] uppercase tracking-[0.2em] text-graphite md:text-[11px]">
          Passive
        </span>
      )}
    </motion.button>

      {/* Opens the full portrait page. The card tile only ever shows the
          square bust crop (portraitArt.ts) because a full-body figure is
          unreadable at tile size — this is how the player gets to see the
          actual drawing, plus the untruncated power text and the tribe it
          pulls toward. Deliberately its own control rather than a long-press:
          hero select is where the player is choosing an identity, so the way
          to look closer should be visible, not discovered. */}
      <button
        type="button"
        onClick={() => openHeroDetail(hero)}
        aria-label={`View ${hero.name} full art and power`}
        className="absolute right-[6px] top-[6px] z-10 flex h-[40px] w-[40px] items-center justify-center rounded-sm border bg-asphalt/70 font-data text-[15px] leading-none text-marker backdrop-blur-sm transition-colors hover:bg-stucco2 hover:text-bone"
        style={{ borderColor: EDGES.faint }}
      >
        ⤢
      </button>
    </div>
  );
}
